import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate, Link } from "react-router-dom";

function BannerList() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ 3 views
  const [view, setView] = useState("active"); // "active" | "inactive" | "brandInactive"

  const baseURL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const token = localStorage.getItem("profileToken");
  const headers = { "x-access-token": token || "" };

  const fetchBanners = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${baseURL}/banner/list`, { headers });
      if (res.data.success || res.data.status) {
        setBanners(res.data.data || res.data.result || []);
      } else {
        setBanners([]);
        setError(res.data.message || "Failed to fetch banners");
      }
    } catch (err) {
      console.error("Error fetching banners:", err);
      setError("Server error while fetching banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
    // eslint-disable-next-line
  }, []);

  // ✅ counts
  const activeCount = useMemo(
    () => banners.filter((b) => Number(b.status) === 1 && Number(b.brand_status) === 1).length,
    [banners]
  );
  const inactiveCount = useMemo(
    () => banners.filter((b) => Number(b.status) === 0).length,
    [banners]
  );
  const brandInactiveCount = useMemo(
    () => banners.filter((b) => Number(b.status) === 1 && Number(b.brand_status) === 0).length,
    [banners]
  );

  // ✅ visible list
  const visibleBanners = useMemo(() => {
    if (view === "inactive") return banners.filter((b) => Number(b.status) === 0);
    if (view === "brandInactive")
      return banners.filter((b) => Number(b.status) === 1 && Number(b.brand_status) === 0);

    // ✅ active view: personal active + brand active
    return banners.filter((b) => Number(b.status) === 1 && Number(b.brand_status) === 1);
  }, [banners, view]);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This banner will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${baseURL}/banner/delete/${id}`, { headers });
      setBanners((prev) => prev.filter((b) => b.id !== id));
      Swal.fire("Deleted!", "Banner has been deleted.", "success");
    } catch {
      Swal.fire("Error!", "Failed to delete banner.", "error");
    }
  };

  const handleToggleStatus = async (banner) => {
    try {
      await axios.patch(`${baseURL}/banner/${banner.id}/status`, {}, { headers });

      const newStatus = Number(banner.status) === 1 ? 0 : 1;

      setBanners((prev) =>
        prev.map((b) => (b.id === banner.id ? { ...b, status: newStatus } : b))
      );

      // ✅ switch view correctly
      if (newStatus === 0) setView("inactive");
      else setView(Number(banner.brand_status) === 0 ? "brandInactive" : "active");

      Swal.fire("Success", "Status updated", "success");
    } catch {
      Swal.fire("Error!", "Failed to update status.", "error");
    }
  };

  if (loading)
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-success" role="status"></div>
        <p className="mt-3">Loading banners...</p>
      </div>
    );

  if (error)
    return (
      <div className="text-center text-danger my-5">
        <p>{error}</p>
      </div>
    );

  const heading =
    view === "inactive" ? "Inactive Banners" : view === "brandInactive" ? "Brand Inactive Banners" : "Banners List";

  const badgeCount = view === "inactive" ? inactiveCount : view === "brandInactive" ? brandInactiveCount : activeCount;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h3 className="mb-0 text-success">
          {heading} <span className="badge bg-secondary ms-2">{badgeCount}</span>
        </h3>

        <div className="d-flex gap-2 flex-wrap">
          {/* Active/Inactive (personal) */}
          <button
            className={`btn ${view === "inactive" ? "btn-outline-success" : "btn-outline-danger"} rounded-pill px-4`}
            onClick={() => setView((p) => (p === "inactive" ? "active" : "inactive"))}
          >
            {view === "inactive" ? `Active (${activeCount})` : `Inactive (${inactiveCount})`}
          </button>

          {/* Brand Inactive */}
          <button
            className={`btn ${view === "brandInactive" ? "btn-warning" : "btn-outline-warning"} rounded-pill px-4`}
            onClick={() => setView((p) => (p === "brandInactive" ? "active" : "brandInactive"))}
          >
            Brand Inactive ({brandInactiveCount})
          </button>

          <Link className="btn btn-success rounded-pill px-4" to="/add-banner">
            <i className="fas fa-plus me-2"></i> Add Banner
          </Link>
        </div>
      </div>

      {/* top note for brandInactive */}
      {view === "brandInactive" && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            These banners are <b>not visible on website</b> because their brands are inactive.
          </div>
          <button className="btn btn-sm btn-danger" onClick={() => navigate("/brands-list?view=inactive")}>
            Go to Inactive Brands
          </button>
        </div>
      )}

      {visibleBanners.length === 0 ? (
        <p className="text-muted">No banners found.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-bordered align-middle text-center">
            <thead className="table-success">
              <tr>
                <th>#</th>
                <th>Brand Name</th>
                <th>Image</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {visibleBanners.map((b, i) => {
                const isBrandInactive = Number(b.brand_status) === 0;

                return (
                  <tr key={b.id} className={view !== "active" ? "table-secondary" : ""} style={view !== "active" ? { opacity: 0.95 } : {}}>
                    <td>{i + 1}</td>

                    <td style={{ textAlign: "left" }}>
                      <div className="fw-semibold">
                        {b.brand_name || "N/A"}
                        {isBrandInactive && <span className="badge bg-danger ms-2">Brand Inactive</span>}
                      </div>

                      {isBrandInactive && (
                        <div className="small text-danger mt-1">
                          For activate this banner you should activate <b>{b.brand_name || "this brand"}</b>.
                        </div>
                      )}
                    </td>

                    <td>
                      <img
                        src={b.image?.startsWith("http") ? b.image : `${baseURL}/uploads/${b.image}`}
                        alt={b.brand_name}
                        style={{ width: "100px", height: "60px", objectFit: "cover", borderRadius: "6px" }}
                      />
                    </td>

                    <td>
                      <div className="form-check form-switch d-flex justify-content-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={Number(b.status) === 1}
                          onChange={() => handleToggleStatus(b)}
                        />
                      </div>
                    </td>

                    <td>
                      <div className="d-flex justify-content-center gap-3">
                        <i className="fas fa-edit text-warning" style={{ cursor: "pointer" }} onClick={() => navigate(`/update-banner/${b.id}`)} title="Edit"></i>
                        <i className="fas fa-trash text-danger" style={{ cursor: "pointer" }} onClick={() => handleDelete(b.id)} title="Delete"></i>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>

          </table>
        </div>
      )}
    </div>
  );
}

export default BannerList;
